import React from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { CohortPersonDetails } from '../cohortPageData'
import { PartnerService, UserService } from '@duck/api-definitions'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { SendAssignment } from './SendAssignment'
import { DeleteDialog } from '../../DeleteDialog'
import { AssignmentProgressTable } from './AssignmentProgressTable'
import { AssignmentActionsDropdown } from './AssignmentActionsDropdown'
import { useAssignmentActions } from './useAssignmentActions'
import { DevPrint } from '@components/DevPrint'

interface AssignmentEntryProps {
  assignment: PartnerService.Assignment
  peopleDetails: CohortPersonDetails[]
  assignmentPersons: UserService.Person[]
  getDisplayName: (taskId: string) => string
  isTaskLoading: boolean
  onAssignmentChange?: () => void
}

export const AssignmentEntry: React.FC<AssignmentEntryProps> = ({
  assignment,
  peopleDetails,
  assignmentPersons,
  getDisplayName,
  isTaskLoading,
  onAssignmentChange,
}) => {
  const { t } = useTar()
  const {
    handleEdit,
    handleDelete,
    handleSetStatus,
    isActive,
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
  } = useAssignmentActions({ assignment, onAssignmentChange })

  const isDueDatePast = assignment.dueDate
    ? new Date(assignment.dueDate) < new Date()
    : false
  const isEffectivelyInactive = !isActive || isDueDatePast

  const finishedCount = peopleDetails.filter(
    (p) => p.assignmentStatus === PartnerService.AssignmentDetailsStatus.Submitted
  ).length
  const completionRate = peopleDetails.length > 0 ? finishedCount / peopleDetails.length : 0

  return (
    <Card
      className={cn(
        isEffectivelyInactive && 'opacity-85 bg-muted/25 border-muted-foreground/50'
      )}
    >
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <CardTitle className="flex-1 text-xl font-semibold">
            {!isActive && (
              <span className="inline-flex items-center rounded-md border border-muted-foreground/50 bg-muted-foreground/20 px-2.5 py-1 text-xs font-semibold text-muted-foreground mr-2">
                {t('common.inactive')}
              </span>
            )}
            {isTaskLoading ? (
              <span className="text-lg font-semibold">{t('common.loading')}</span>
            ) : (
              getDisplayName(assignment.taskId)
            )}
            <DevPrint label="assignmentId" value={assignment.id} />
          </CardTitle>
          <div className="flex items-center gap-2">
            <AssignmentActionsDropdown
              isActive={!isEffectivelyInactive}
              onEdit={handleEdit}
              onDeleteClick={() => setIsDeleteDialogOpen(true)}
              onSetStatus={handleSetStatus}
            />
          </div>
        </div>
        <CardDescription className="space-y-3 pt-2">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row">
              <div className="flex flex-col gap-2 font-bold text-foreground">{t('partner.timeWindow')}:</div>
              <div className="text-right flex-1">
                <div className="flex-row flex items-center justify-end gap-2">
                  <div className="hidden md:flex items-center text-sm">
                    <span className="font-medium">{t('partner.completionRate')}:</span>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5">
                          <svg width="24" height="24" viewBox="0 0 24 24" className="transform -rotate-90">
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              className="text-muted/30"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="4"
                              strokeDasharray={`${completionRate * 62.83} 62.83`}
                              strokeLinecap="round"
                              className="text-primary"
                            />
                          </svg>
                          <span className="text-xs text-muted-foreground">
                            {finishedCount}/{peopleDetails.length}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <span>{Math.round(completionRate * 100)}% {t('partner.completionRate').toLowerCase()}</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
            <div className="flex flex-col flex-wrap gap-2 lg:gap-4 text-sm">
              {assignment.startDate && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{t('partner.startDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.startDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {assignment.dueDate && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{t('partner.dueDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.dueDate}
                    className="text-muted-foreground"
                  />
                  {isDueDatePast && (
                    <span className="inline-flex items-center rounded-md border border-muted-foreground/50 bg-muted-foreground/20 px-2.5 py-1 text-xs font-semibold text-muted-foreground">
                      {t('challenges.finished')}
                    </span>
                  )}
                </div>
              )}
              {assignment.overdueDate && (
                <div>
                  <span className="font-medium">{t('partner.overdueDate')}:</span>{' '}
                  <RelativeDateTime
                    isoDateString={assignment.overdueDate}
                    className="text-muted-foreground"
                  />
                </div>
              )}
              {assignment.timeLimit != null && assignment.timeLimit > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{t('partner.timeLimit')}:</span>{' '}
                  <span className="text-muted-foreground">
                    {(() => {
                      const totalMinutes = assignment.timeLimit ?? 0
                      const isWholeHours = totalMinutes % 60 === 0
                      const amount = isWholeHours ? totalMinutes / 60 : totalMinutes
                      const isSingular = amount === 1
                      const unitLabel = isWholeHours
                        ? (isSingular ? t('common.hour') : t('common.hours'))
                        : (isSingular ? t('common.minute') : t('common.minutes'))
                      return `${amount} ${unitLabel}`
                    })()}
                  </span>
                </div>
              )}
              {!assignment.startDate && !assignment.dueDate && !assignment.overdueDate && !assignment.timeLimit && (
                <div className="text-muted-foreground">{t('partner.noRestriction')}</div>
              )}
            </div>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        {peopleDetails.length > 0 ? (
          <AssignmentProgressTable
            peopleDetails={peopleDetails}
            persons={assignmentPersons}
            sentTime={assignment.createdAt || ''}
            inactive={isEffectivelyInactive}
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{t('common.empty')}</p>
          </div>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent width="xs">
          <DialogHeader>
            <DialogTitle>{t('common.editAssignment')}</DialogTitle>
          </DialogHeader>
          <SendAssignment
            cohortId={assignment.cohortId}
            personIds={assignment.personIds}
            assignmentId={assignment.id}
            onClose={() => setIsEditDialogOpen(false)}
            onSend={() => {
              setIsEditDialogOpen(false)
              onAssignmentChange?.()
            }}
          />
        </DialogContent>
      </Dialog>

      <DeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        title={t('common.deleteAssignment')}
        description={t('common.assignmentDeletedDescription')}
      />
    </Card>
  )
}
